import {
  convertPriceText,
  isDisplayCurrency,
  type CurrencyRates,
  type DisplayCurrency,
} from "./currency-rates";

// The currency a visitor chose in the header, kept in this browser for each site, and the prices
// on the page rewritten in it. Pages are drawn and cached in the creator's own currencies, so the
// change happens here, after the page runs; checkout still charges the creator's currency.

const storageKey = (creatorId: string) => `drate-currency:${creatorId}`;
const changeEvent = "drate:currency";

// Where prices are drawn: shop cards, the product page, bag amounts, page and Home product
// cards, membership and course prices. `data-money` marks any other. The payment step and order
// pages (`data-no-convert`) keep the currency that is charged.
const priceSelector = [
  ".shop-card-price",
  ".shop-price-row",
  ".shop-amount",
  ".drate-page-price",
  ".membership-price",
  ".plan-price",
  ".plan-then",
  ".preview-price",
  ".course-price",
  ".demo-checkout-price",
  ".bh-card-price",
  "[data-money]",
].join(",");

// A choice the browser refused to keep (blocked site data, a full quota), which every picker
// on this page still reads until it closes.
const unsaved = new Map<string, DisplayCurrency | null>();

export function readDisplayCurrency(creatorId: string): DisplayCurrency | null {
  if (unsaved.has(creatorId)) return unsaved.get(creatorId) ?? null;
  try {
    const value = localStorage.getItem(storageKey(creatorId));
    return isDisplayCurrency(value) ? value : null;
  } catch {
    return null;
  }
}

export function chooseDisplayCurrency(creatorId: string, currency: DisplayCurrency | null) {
  try {
    if (currency) localStorage.setItem(storageKey(creatorId), currency);
    else localStorage.removeItem(storageKey(creatorId));
    unsaved.delete(creatorId);
  } catch {
    // Private windows may refuse storage; the choice still holds for this page.
    unsaved.set(creatorId, currency);
  }
  document.dispatchEvent(new CustomEvent(changeEvent, { detail: { creatorId, currency } }));
}

export function subscribeDisplayCurrency(update: () => void) {
  const onStorage = (event: StorageEvent) => {
    if (!event.key || event.key.startsWith("drate-currency:")) update();
  };
  document.addEventListener(changeEvent, update);
  window.addEventListener("storage", onStorage);
  return () => {
    document.removeEventListener(changeEvent, update);
    window.removeEventListener("storage", onStorage);
  };
}

// Each price text as the page drew it, and what this script last wrote in its place, so a
// redraw by the page (a new variant's price) is read afresh and a change of currency can undo.
const written = new WeakMap<Text, { source: string; shown: string }>();

function rewrite(node: Text, currency: DisplayCurrency | null, rates: CurrencyRates | null) {
  const current = node.nodeValue ?? "";
  const known = written.get(node);
  const source = known && known.shown === current ? known.source : current;
  const shown = currency && rates ? convertPriceText(source, currency, rates) : source;
  if (shown !== current) node.nodeValue = shown;
  written.set(node, { source, shown });
}

function rewriteWithin(root: Node, currency: DisplayCurrency | null, rates: CurrencyRates | null) {
  const element = root instanceof Element ? root : root.parentElement;
  if (!element) return;
  const scopes = element.closest(priceSelector)
    ? [element]
    : [...element.querySelectorAll(priceSelector)];
  for (const scope of scopes) {
    if (scope.closest("[data-no-convert]")) continue;
    const walker = document.createTreeWalker(scope, NodeFilter.SHOW_TEXT);
    for (let node = walker.nextNode(); node; node = walker.nextNode())
      rewrite(node as Text, currency, rates);
  }
}

// Whether `scope` shows an amount this script wrote in another currency than the page drew.
function converted(scope: Node) {
  const walker = document.createTreeWalker(scope, NodeFilter.SHOW_TEXT);
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    const known = written.get(node as Text);
    if (known && known.shown !== known.source && known.shown === node.nodeValue) return true;
  }
  return false;
}

// "Checkout charges the shop's own currency": shown beside a bag group's totals while its
// amounts are converted, and not over amounts already in the chosen currency.
function showNotes() {
  for (const note of document.querySelectorAll<HTMLElement>("[data-currency-note]"))
    note.hidden = !converted(note.closest(".shop-group") ?? document.body);
}

// One watcher for the page, however many pickers it shows (header, menu sheet, footer).
let watching: {
  currency: DisplayCurrency | null;
  rates: CurrencyRates | null;
  observer: MutationObserver | null;
  users: number;
} | null = null;

/**
 * Shows every price on the page in `currency` (null: as the creator set them) and keeps doing so
 * as the page changes. Returns a function that stops watching.
 */
export function showPricesIn(currency: DisplayCurrency | null, rates: CurrencyRates | null) {
  if (watching && watching.currency === currency && watching.rates === rates) {
    const shared = watching;
    shared.users++;
    return () => {
      if (--shared.users === 0) {
        shared.observer?.disconnect();
        if (watching === shared) watching = null;
      }
    };
  }
  watching?.observer?.disconnect();
  const view = { currency, rates, observer: null as MutationObserver | null, users: 1 };
  watching = view;
  rewriteWithin(document.body, currency, rates);
  showNotes();
  if (currency && rates) {
    view.observer = new MutationObserver((records) => {
      for (const record of records)
        if (record.type === "characterData") rewriteWithin(record.target, currency, rates);
        else for (const node of record.addedNodes) rewriteWithin(node, currency, rates);
      showNotes();
    });
    view.observer.observe(document.body, { subtree: true, childList: true, characterData: true });
  }
  return () => {
    if (--view.users === 0) {
      view.observer?.disconnect();
      if (watching === view) watching = null;
    }
  };
}
